<template>
  <div v-if="component">

    <!-- ── TOOLBAR ──────────────────────────────────────────────── -->
    <div class="dl-toolbar" v-if="currentPreset">
      <div class="dl-toolbar__left">
        <b-btn variant="success" size="sm" @click="goToCreate" v-if="$can('create ' + component.general.getRef())">
          <span v-if="$screen.md">{{ $t('create') }}</span>
          <span v-else>+</span>
        </b-btn>

        <b-dropdown size="sm" variant="outline-secondary" text="⋮">
          <b-dropdown-item @click="importData.modal = true">{{ $t('import') }}</b-dropdown-item>
          <b-dropdown-item @click="runExport">{{ $t('export') }}</b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          v-if="itemsSelected.length > 0"
          size="sm"
          variant="outline-secondary"
          :text="`${$t('choose')}...`">
          <b-dropdown-item @click="multiAction('delete')">{{ $t('delete') }}</b-dropdown-item>
          <template v-if="settings.model.disable">
            <b-dropdown-item @click="multiAction('disable')">{{ $t('disable') }}</b-dropdown-item>
            <b-dropdown-item @click="multiAction('enable')">{{ $t('enable') }}</b-dropdown-item>
          </template>
          <b-dropdown-divider />
          <b-dropdown-item @click="transferToEnv">
            {{ currentEnvIsLive ? $t('transferToDev') : $t('transferToLive') }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div class="dl-toolbar__right">
        <b-select
          v-model="selectedSchema"
          :options="availableSchemas"
          @change="onSchemaChange"
          size="sm"
          class="dl-schema-select">
        </b-select>

        <b-input-group size="sm" class="dl-search-group">
          <b-form-input
            :value="currentPreset.context.filters.filter"
            @input="onSearch"
            type="search"
            :placeholder="`${$t('search')}`">
          </b-form-input>
          <b-input-group-append v-if="currentPreset.context.filters.filter">
            <b-button variant="outline-secondary" @click="clearSearch">×</b-button>
          </b-input-group-append>
        </b-input-group>

        <!-- Sort dropdown -->
        <b-dropdown
          size="sm"
          variant="outline-secondary"
          class="dl-sort-dropdown"
          no-caret
          right>
          <template #button-content>
            <b-icon :icon="sortDesc ? 'sort-down' : 'sort-up'" class="mr-1" /><span class="dl-sort-label">{{ currentSortLabel }}</span>
          </template>
          <b-dropdown-item
            v-for="option in sortFieldOptions"
            :key="option.value"
            @click="onSortSelect(option.value)"
            :active="sortBy === option.value"
            class="dl-sort-option">
            <span class="dl-sort-option__text">{{ option.text }}</span>
            <b-icon v-if="sortBy === option.value" :icon="sortDesc ? 'arrow-down' : 'arrow-up'" class="dl-sort-option__icon" />
          </b-dropdown-item>
        </b-dropdown>

        <b-button size="sm" variant="outline-secondary" @click="toggleFiltersPanel" class="dl-filter-toggle">
          <b-icon icon="sliders" />
          {{ $t('filters') }}
          <b-badge v-if="activeFilterCount > 0" variant="primary" pill class="ml-1">{{ activeFilterCount }}</b-badge>
        </b-button>

        <c-switch
          v-if="settings.model.disable"
          class="mx-1"
          color="success"
          variant="pill"
          :label="true"
          :dataOn="`${$t('yes')}`"
          :dataOff="`${$t('no')}`"
          v-model="showDisabled" />
      </div>
    </div>

    <!-- ── FILTER PANEL ─────────────────────────────────────────── -->
    <b-collapse v-model="filtersPanelOpen" v-if="currentPreset">
      <div class="dl-filter-panel mt-2 p-3">

        <b-row v-if="!disablePresets" class="mb-3">
          <b-col cols="12" md="8">
            <b-form-group :label="`${$t('preset')}`" label-cols-md="2" class="mb-0">
              <b-row>
                <b-col cols="7">
                  <b-select v-model="filterPreset" :options="availablePresets" size="sm"></b-select>
                </b-col>
                <b-col cols="5" align-self="center">
                  <b-dropdown size="sm" :text="$t('options')" variant="link" class="m-0 p-0">
                    <b-dropdown-item v-if="filterPreset != 'Default'" @click="updatePreset()">{{ $t('update') }}</b-dropdown-item>
                    <b-dropdown-item v-if="filterPreset != 'Default'" @click="deletePreset()">{{ $t('delete') }}</b-dropdown-item>
                    <b-dropdown-item v-if="!currentPreset.context.default && filterPreset != 'Default'" @click="setAsDefault()">{{ $t('setDefault') }}</b-dropdown-item>
                    <b-dropdown-item @click="fork.modal = true">{{ $t('fork') }}</b-dropdown-item>
                    <b-dropdown-item to="settings#list" :append="true">{{ $t('settings.title') }}</b-dropdown-item>
                  </b-dropdown>
                  <main-dialog hide-header v-model="fork.modal" ok-only :ok-title="`${$t('fork')}`" @ok="forkIt">
                    <b-input v-model="fork.name" :placeholder="`${$t('newFilterName')}`" />
                  </main-dialog>
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>
        </b-row>

        <hr v-if="!disablePresets" class="my-2" />

        <b-row>
          <b-col lg="3" md="6">
            <b-form-group :label="`${$t('field')}:`" label-cols-sm="3" class="mb-2">
              <b-form-select :options="dateFieldOptions" v-model="pendingFilters.dateField" size="sm"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col lg="3" md="6">
            <b-form-group :label="`${$t('from')}:`" label-cols-sm="3" class="mb-2">
              <b-form-datepicker
                :date-format-options="dateFormat"
                locale="en-US"
                :label-no-date-selected="$t('noDateSelected')"
                v-model="pendingFilters.dateFrom"
                reset-button>
              </b-form-datepicker>
            </b-form-group>
          </b-col>
          <b-col lg="3" md="6">
            <b-form-group :label="`${$t('to')}:`" label-cols-sm="3" class="mb-2">
              <b-form-datepicker
                :date-format-options="dateFormat"
                locale="en-US"
                :label-no-date-selected="$t('noDateSelected')"
                v-model="pendingFilters.dateTo"
                reset-button>
              </b-form-datepicker>
            </b-form-group>
          </b-col>
          <b-col lg="3" md="6">
            <AdvancedFilters v-model="pendingExpr" @apply="applyPendingFiltersWithExpr" />
          </b-col>
        </b-row>

        <b-row v-if="availableFilterFields.length > 0" class="mb-2">
          <b-col>
            <b-form-group
              :label="`${$t('filterOn')}`"
              label-cols-sm="2"
              :description="`${$t('leaveUnchecked')}`"
              class="mb-0">
              <b-form-checkbox-group v-model="pendingFilters.filterOn" class="mt-1">
                <b-form-checkbox
                  v-for="field in availableFilterFields"
                  :value="field.key"
                  v-bind:key="field.key">
                  {{ field.label || field.key }}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="dl-filter-panel__footer">
          <b-btn size="sm" variant="outline-secondary" @click="resetPendingFilters">{{ $t('reset') }}</b-btn>
          <b-btn size="sm" variant="primary" @click="applyPendingFilters">
            {{ $t('apply') }}<span v-if="pendingChangeCount > 0"> ({{ pendingChangeCount }})</span>
          </b-btn>
        </div>
      </div>
    </b-collapse>

    <!-- ── ACTIVE FILTER CHIPS ───────────────────────────────────── -->
    <div class="dl-active-filters mt-2" v-if="activeFilterChips.length > 0 && currentPreset">
      <span class="dl-chip" v-for="chip in activeFilterChips" :key="chip.key">
        {{ chip.label }}
        <span class="dl-chip__remove" @click="removeFilterChip(chip.key)">×</span>
      </span>
      <b-btn variant="link" size="sm" class="p-0 ml-2" @click="clearAllFilters">{{ $t('clearAll') }}</b-btn>
    </div>

    <!-- ── CARD + TABLE ──────────────────────────────────────────── -->
    <b-card show-footer v-if="currentPreset" class="mt-2">
      <div class="dl-table-wrap" :class="{ 'dl-table-wrap--refreshing': isRefreshing }">
        <div v-if="isRefreshing && !busy" class="dl-refresh-overlay">
          <div class="dl-refresh-spinner"></div>
        </div>

      <PkDataTable
        :columns="availableFields"
        :rows="items"
        :loading="busy"
        row-key="uuid"
        @row-click="edit">

        <template v-slot:empty>
          <p class="pk-dt__empty-text mb-3">{{ $t('noResultsForFilter') }}</p>
          <b-button size="sm" variant="outline-primary" @click="toggleFiltersPanel">{{ $t('updateFilters') }}</b-button>
        </template>

        <template v-slot:head(multiselect)>
          <b-form-checkbox
            v-model="selectAll"
            @change="toggleSelectAll"
            :indeterminate="isIndeterminate">
          </b-form-checkbox>
        </template>

        <template v-slot:cell(multiselect)="{ row }">
          <b-form-group :label-for="row.uuid" @click.stop>
            <b-form-checkbox
              :id="row.uuid"
              :name="row.uuid"
              :value="row.uuid"
              v-model="itemsSelected"
              style="position: relative">
            </b-form-checkbox>
          </b-form-group>
        </template>

        <template v-slot:[fieldCell(field.key)]="{ row }" v-for="field in availableIdentityFields">
          <ComponentView
            v-if="field.customListField && field.component_template && field.provided_template_component"
            :id="field.provided_template_component"
            :entrypoint="field.component_template"
            :key="field.key"
            :item="row" />
          <span v-else-if="field.formatter">
            {{ formattedValueForField(field, row) }}
          </span>
          <ListFormatterResolver :field="field" :item="row" v-else />
        </template>

        <template v-slot:cell(actions)="{ row }">
          <b-col cols="12" align-self="center" style="cursor:pointer">
            <b-icon icon="pencil" @click.stop="quickActionEdit($event, row)" />
          </b-col>
        </template>
      </PkDataTable>

      </div><!-- /.dl-table-wrap -->

      <b-dropdown
        v-if="itemsSelected.length > 0"
        size="sm"
        :text="`${$t('choose')}...`"
        variant="link"
        class="m-0">
        <b-dropdown-item @click="multiAction('delete')">{{ $t('delete') }}</b-dropdown-item>
        <template v-if="settings.model.disable">
          <b-dropdown-item @click="multiAction('disable')">{{ $t('disable') }}</b-dropdown-item>
          <b-dropdown-item @click="multiAction('enable')">{{ $t('enable') }}</b-dropdown-item>
        </template>
        <b-dropdown-divider />
        <b-dropdown-item @click="transferToEnv">
          {{ currentEnvIsLive ? $t('transferToDev') : $t('transferToLive') }}
        </b-dropdown-item>
      </b-dropdown>

      <div slot="footer">
        <nav v-if="totalItems">
          <b-pagination :total-rows="totalItems" :per-page="10" v-model="page" />
          <span>{{ $t('total') }} : {{ totalItems }}</span>
          <span v-if="totalItems >= 5000">+</span>
          <br />
          <small class="text-muted" v-if="totalItems >= 5000">{{ $t('itemsTotalDisclaimer') }}</small>
        </nav>
      </div>

      <b-sidebar right v-model="editModel.show" no-backdrop width="500px">
        <Edit
          :id="componentID"
          :modelUuid="editModel.model.uuid"
          v-if="editModel.model"
          :key="editModel.model.uuid"
          :settings="{ container: { cols: 12, md: 12 } }" />
      </b-sidebar>
    </b-card>
    <b-card v-else>
      {{ $t('noDefaultPreset') }}
    </b-card>

    <!-- ── IMPORT MODAL ──────────────────────────────────────────── -->
    <main-dialog v-model="importData.modal" :title="`${$t('import')}`" @ok="runImport()" :ok-title="`${$t('import')}`">
      <b-form-group label="File">
        <b-form-file v-model="importData.file" />
      </b-form-group>
      <b-form-group label="Field map" description="Map fields from imported file to component fields">
        <b-row v-for="(i, f) in Object.keys(importData.fieldMap)" :key="f" class="mb-2">
          <b-col cols="4"><b-input v-model="importData.fieldMap[i].from" /></b-col>
          <b-col cols="4">
            <b-form-select
              v-model="importData.fieldMap[i].to"
              :options="availableFields.map(function(ff) { return { text: ff.key, value: ff.key } })" />
          </b-col>
          <b-col cols="4" @click="removeFieldMap(i)"><i class="fa fa-trash"></i></b-col>
        </b-row>
        <b-button @click="addFieldMap">{{ $t('add') }}</b-button>
      </b-form-group>
      <b-form-group
        label="Primary ID"
        description="Fields that match with this primary id field will be instead updated and not created as new. If left blank all models will be created as new.">
        <b-form-select
          v-model="importData.primaryKey"
          :options="availableFields.map(function(ff) { return { text: ff.key, value: ff.key } })" />
      </b-form-group>
      <HowItWorksPopup>{{ $t('fieldsCsv') }}</HowItWorksPopup>
    </main-dialog>

  </div>
</template>

<style scoped>
/* ── TOOLBAR ────────────────────────────────────────────────────── */
.dl-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 8px;
}

.dl-toolbar__left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dl-toolbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-end;
}

.dl-schema-select {
  max-width: 160px;
}

.dl-search-group {
  max-width: 260px;
}

.dl-filter-toggle {
  white-space: nowrap;
}

/* ── SORT DROPDOWN ──────────────────────────────────────────────── */
.dl-sort-dropdown .btn {
  white-space: nowrap;
  max-width: 180px;
}

.dl-sort-label {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
  display: inline-block;
  vertical-align: middle;
}

.dl-sort-option {
  min-width: 180px;
}

.dl-sort-option .dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dl-sort-option__text {
  flex: 1;
}

.dl-sort-option__icon {
  margin-left: 8px;
  opacity: 0.7;
}

/* ── FILTER PANEL ───────────────────────────────────────────────── */
.dl-filter-panel {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.dl-filter-panel__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* ── ACTIVE FILTER CHIPS ────────────────────────────────────────── */
.dl-active-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.dl-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #e8f0fe;
  border: 1px solid #c5d3f5;
  border-radius: 12px;
  font-size: 0.8rem;
  color: #3c5bab;
}

.dl-chip__remove {
  cursor: pointer;
  font-weight: bold;
  line-height: 1;
  opacity: 0.65;
  margin-left: 2px;
}

.dl-chip__remove:hover {
  opacity: 1;
}

/* ── REFRESH OVERLAY ─────────────────────────────────────────────── */
.dl-table-wrap {
  position: relative;
}

.dl-table-wrap--refreshing {
  pointer-events: none;
}

.dl-table-wrap--refreshing .b-table {
  opacity: 0.4;
  transition: opacity 0.15s;
}

.dl-refresh-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
}

.dl-refresh-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(108, 128, 255, 0.2);
  border-top-color: #6c80ff;
  border-radius: 50%;
  animation: dl-spin 0.7s linear infinite;
  box-shadow: 0 0 12px rgba(108, 128, 255, 0.4);
}

@keyframes dl-spin {
  to { transform: rotate(360deg); }
}

/* ── SKELETON ROWS ───────────────────────────────────────────────── */
.dl-skeleton-container {
  padding: 4px 0;
}

.dl-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid var(--border-separator);
}

.dl-skeleton-row:last-child {
  border-bottom: none;
}

.dl-skeleton-cell {
  flex: 1;
  min-width: 0;
}

.dl-skeleton-bar {
  height: 13px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    #383a40 25%,
    #44474e 50%,
    #383a40 75%
  );
  background-size: 300% 100%;
  animation: dl-shimmer 1.6s ease-in-out infinite;
}

@keyframes dl-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -100% 0; }
}

/* ── TABLE ──────────────────────────────────────────────────────── */
.b-table-sticky-column:last-child {
  right: 0;
}
</style>

<script>
import safeEval from "safe-eval"
import { Switch as cSwitch } from '@coreui/vue'
import _ from 'lodash'
import AdvancedFilters from './AdvancedFilters'
import ListFormatterResolver from '../listFormatters/Resolver'
import moment from 'moment'
import Filters from '../libs/filters'
import HowItWorksPopup from '../elements/HowItWorksPopup'
import Edit from "./Edit.vue"
import ComponentView from "./ComponentView.vue"
import PkDataTable from '../PkDataTable.vue'

// Module-level list cache — survives component unmount/remount
const _listCache = new Map()
const _LIST_CACHE_TTL = 30 * 1000 // 30 seconds
const _cursorsByPage = new Map()

function _defaultDateFrom() {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  return d.toISOString().slice(0, 10)
}

function _defaultDateTo() {
  return new Date().toISOString().slice(0, 10)
}

function _listCacheKey(componentID, filters) {
  return componentID + '::' + JSON.stringify({
    page: filters.currentPage,
    perPage: filters.perPage,
    sortBy: filters.sortBy,
    sortDesc: filters.sortDesc,
    filter: filters.filter,
    filterOn: filters.filterOn,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    dateField: filters.dateField,
    $adv: filters.$adv,
    disabled: filters.disabled,
  })
}

function _cursorBaseKey(componentID, filters) {
  return componentID + '::cursor::' + JSON.stringify({
    perPage: filters.perPage,
    sortBy: filters.sortBy,
    sortDesc: filters.sortDesc,
    filter: filters.filter,
    filterOn: filters.filterOn,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    dateField: filters.dateField,
    $adv: filters.$adv,
    disabled: filters.disabled,
  })
}

function _filterStateKey(componentID) {
  return 'dl_filters_' + componentID
}

function _saveFilterState(componentID, state) {
  try {
    sessionStorage.setItem(_filterStateKey(componentID), JSON.stringify(state))
  } catch (e) { /* quota exceeded — ignore */ }
}

function _restoreFilterState(componentID) {
  try {
    const raw = sessionStorage.getItem(_filterStateKey(componentID))
    return raw ? JSON.parse(raw) : null
  } catch (e) { return null }
}

export function invalidateListCache(componentID) {
  const prefix = componentID + '::'
  const schemaPrefix = componentID + '.'
  for (const key of _listCache.keys()) {
    if (key.startsWith(prefix) || key.startsWith(schemaPrefix)) {
      _listCache.delete(key)
    }
  }
  for (const key of _cursorsByPage.keys()) {
    if (key.startsWith(prefix) || key.startsWith(schemaPrefix)) {
      _cursorsByPage.delete(key)
    }
  }
}

export default {
  components: {
    cSwitch,
    ListFormatterResolver,
    ComponentView,
    AdvancedFilters,
    HowItWorksPopup,
    Edit,
    PkDataTable
  },
  props: {
    component: {
      type: Object,
      default: () => null
    },

    currentPage: {
      type: Number,
      default: 1
    },

    id: {
      type: String,
      default: null
    },

    disablePresets: {
      type: Boolean,
      default: false
    },

    activePreset: {
      type: String,
      default: 'Default'
    }
  },
  data: () => {
    return {
      tableReady: false,
      _mountingState: true,
      items: [],
      showDisabled: false,
      totalItems: null,
      busy: true,
      itemsSelected: [],
      dateFormat: { year: 'numeric', month: 'numeric', day: '2-digit' },
      filtersCopy: null,
      presets: [],
      filterPreset: null,
      selectedSchema: null,
      filtersPanelOpen: false,
      isRefreshing: false,
      sortBy: 'updated_at',
      sortDesc: true,
      pendingFilters: {
        dateField: 'updated_at',
        dateFrom: _defaultDateFrom(),
        dateTo: _defaultDateTo(),
        filterOn: [],
        $adv: {}
      },
      pendingExpr: '',
      fork: {
        modal: false,
        name: ''
      },
      defaultPreset: {
        context: {
          filters: {
            dateField: 'created_at',
            dateFrom: null,
            dateTo: null,
            filter: '',
            filterOn: [],
            $adv: {}
          },
          expr: ''
        },
        name: 'Default'
      },
      importData: {
        modal: false,
        file: null,
        primaryKey: null,
        fieldMap: []
      },
      editModel: {
        model: null,
        show: false
      },

      forceShowHeader: false,
      currentPageItems: []
    }
  },
  watch: {

    showDisabled() {
      this.fetchData()
    },

    '$route.query.page'() {
      if (this._mountingState) return
      this.fetchData()
    },

    filterPreset(val, oldVal) {
      // Always sync pendingFilters when preset changes
      if (this.currentPreset) {
        this.pendingFilters = _.cloneDeep(this.currentPreset.context.filters)
        this.pendingExpr = this.currentPreset.context.expr || ''
      }

      // Skip actual filtering on initial value assignment during mount
      if (oldVal === null) {
          return
      }

      if (this.$router) {
        this.$router.push({ query: { ...this.$route.query, preset: val } })
      }

      this.filterOnChange()
    }
  },
  computed: {
    currentEnvIsLive() {
      return this.$store.getters.getCurrentEnv === 'live'
    },

    availableSchemas() {
      const schemas = new Set()
      schemas.add('') // Add default schema

      if (this.settings.fields) {
        this.settings.fields.forEach(field => {
          if (field.schema) {
            schemas.add(field.schema)
          }
        })
      }

      const extensions = (this.settings.extensions || []).filter(ext => ext.is_active)
      extensions.forEach(extension => {
        if (extension.fields) {
          extension.fields.forEach(field => {
            if (field.schema) {
              schemas.add(field.schema)
            }
          })
        }
      })

      const schemaOptions = Array.from(schemas).sort().map(schema => ({
        text: schema || this.$t('default'),
        value: schema || ''
      }))

      return schemaOptions
    },

    settings() {
      return this.component.settings;
    },

    totalRows: function () { return this.pages },
    itemsFiltered() {
      return this.items.filter(i => {
        return (i.disabled_at === null) != this.showDisabled
      })
    },

    availableFilterFields() {
      let fields = this.availableFields

      if (fields !== undefined) {
        return fields.filter(f => {
          return f.filterable
        })
      }

      return []
    },

    showHeader() {
      if (this.forceShowHeader) return true;

      return this.$screen.md && this.$screen.lg
    },

    availableIdentityFields() {
      let fields = this.availableFields

      if (fields !== undefined) {
        return fields.filter(f => {
          return f.key != 'multiselect'
        })
      }

      return []
    },

    dateFieldOptions() {
      let def = [
        {
          text: this.$t('createdAt'),
          value: "created_at"
        },
        {
          text: this.$t('updatedat'),
          value: "updated_at"
        }
      ]

      if (this.settings.fields !== null) {
        this.settings.fields.forEach(f => {
          if (f.type == "date" && f.module == "picker") {
            def.push({
              text: this.$translateLabel(f.label),
              value: f.key
            })
          }

          if (f.type == "datetime" && f.module == "picker") {
            def.push({
              text: this.$translateLabel(f.label),
              value: f.key
            })
          }
        })
      }

      return def
    },

    customDateFields() {
      let r = []

      try {
        let fields = _.get(this.settings, 'fields', []) || []

        fields.forEach(f => {
          if ((f.type == "date" && f.module == "picker") || (f.type == "datetime" && f.module == "picker")) {
            r.push(f.key)
          }
        })

        return r
      } catch (err) {
        this.$debugLog(err)
        this.$mainDialog.msgBoxOk(this.$t('confNotValid'))
      }
    },

    availablePresets() {
      return this.presets.map(p => {
        return {
          text: p.name,
          value: p.name
        }
      })
    },

    filters: {
      get() {
        let filters = {
          filter: "",
          filterOn: [],
          dateField: 'created_at',
          dateFrom: null,
          dateTo: null,
          expr: '',
          $adv: {}
        }

        let currentPreset = this.currentPreset
        if (currentPreset) {
          filters = currentPreset.context
        }

        if (this.presets) {
          let preset = this.presets.find(p => {
            return this.filterPreset == p.name
          })

          if (preset !== undefined) {
            let advancedFilters = preset.context

            if (filters.expr != "") {
              advancedFilters = safeEval("(function() { try {" + filters.expr + "} catch(error) { } }())", {
                filters: filters
              })

              if (advancedFilters === undefined) {
                return filters
              }

              return advancedFilters
            }

            return filters
          }
        }

        return filters
      },

      set(val) {
        this.currentPreset.context = val
      }
    },

    currentPreset() {
      let preset = this.presets.find(p => {
        return this.filterPreset == p.name
      })

      if (!preset && this.$isAdmin()) {
        return {
          context: {
            filters: {
              dateField: 'created_at',
              dateFrom: null,
              dateTo: null,
              filter: '',
              filterOn: [],
              $adv: {}
            },
            expr: ''
          },
          name: 'Default'
        }
      }

      return preset
    },

    page: {
      get() {
        if (this.$route) {
          let currentPage = this.$route.query.page
          if (currentPage) {
            return parseInt(currentPage, 10)
          }
        }

        return this.currentPage
      },

      set(val) {
        if (this.$router) {
          this.$router.push({ query: { ...this.$route.query, page: val } })
        } else {
          this.currentPage = val
        }

        this.$emit('pageChanged', val)
      }
    },

    availableFields() {
      const listFields = _.cloneDeep(this.settings.list.fields) || []
      let componentFields = this.settings.fields || []

      // Include fields from active extensions (with namespaced keys)
      const extensions = (this.settings.extensions || []).filter(ext => ext.is_active)
      extensions.forEach(extension => {
        if (extension.fields) {
          extension.fields.forEach(field => {
            componentFields = componentFields.concat([{
              ...field,
              key: `extensions.${extension.name}.${field.key}`,
              originalKey: field.key,
              extensionName: extension.name,
              isExtensionField: true
            }])
          })
        }
      })

      let fields = []

      // Filter fields by selected schema
      if (this.selectedSchema !== null) {
        const schemaToFilter = this.selectedSchema
        componentFields = componentFields.filter(f => (f.schema || '') === schemaToFilter)
      }

      if (!listFields || listFields.length < 1) {
        fields = _.cloneDeep(componentFields)
      } else {
        // Filter list fields by schema as well
        const baseFields = componentFields.filter(f => !f.isExtensionField)
        fields = listFields.filter(lf => {
          const componentField = baseFields.find(cf => cf.key === lf.key)
          return componentField !== undefined
        })
        // Always append extension fields — they are not tracked in listFields
        const extensionFields = componentFields.filter(f => f.isExtensionField)
        fields = fields.concat(extensionFields)
      }

      fields.sort((a, b) => {
        return a.sortOrder - b.sortOrder
      })

      fields.push({
        key: "created_at",
        label: this.$t('createdAt')
      })

      fields.push({
        key: "updated_at",
        label: this.$t('updatedAt')
      })

      let f = fields.map(f => {
        f.label = (typeof f.label === 'string' && f.label !== '')
          ? f.label
          : this.$translateLabel(f.label, f.name)
        f.thStyle = { whiteSpace: 'nowrap' }

        const exists = componentFields.find(cf => cf.key === f.key)
        if (!exists && f.key !== 'created_at' && f.key !== 'updated_at') {
          f.customListField = true
        }
        f.sortable = false

        return f
      })

      f.unshift({
        key: "multiselect",
        label: ""
      })

      f.unshift({
        key: "actions",
        label: "",
      })

      return f
    },

    componentID() {
      return this.id || (this.$route ? this.$route.meta.ref : null)
    },

    selectAll: {
      get() {
        if (this.currentPageItems.length === 0) return false
        return this.currentPageItems.every(item => this.itemsSelected.includes(item.uuid))
      },
      set(value) {
        // Handled in toggleSelectAll method
      }
    },

    isIndeterminate() {
      if (this.currentPageItems.length === 0) return false
      const selectedCount = this.currentPageItems.filter(item => this.itemsSelected.includes(item.uuid)).length
      return selectedCount > 0 && selectedCount < this.currentPageItems.length
    },

    activeFilterChips() {
      if (!this.currentPreset) return []
      var filters = this.currentPreset.context.filters
      var chips = []

      if (filters.dateFrom) {
        chips.push({ key: 'dateFrom', label: this.$t('from') + ': ' + filters.dateFrom })
      }
      if (filters.dateTo) {
        chips.push({ key: 'dateTo', label: this.$t('to') + ': ' + filters.dateTo })
      }
      if (filters.filterOn && filters.filterOn.length > 0) {
        chips.push({ key: 'filterOn', label: this.$t('filterOn') + ': ' + filters.filterOn.join(', ') })
      }
      if (this.currentPreset.context.expr && this.currentPreset.context.expr.trim()) {
        chips.push({ key: 'expr', label: 'Advanced filter' })
      }
      if (filters.filter && filters.filter.trim()) {
        chips.push({ key: 'filter', label: this.$t('search') + ': ' + filters.filter })
      }
      if (this.sortBy && this.sortBy !== 'created_at') {
        var dir = this.sortDesc ? '↓' : '↑'
        chips.push({ key: 'sort', label: this.$t('sort') + ': ' + this.sortBy + ' ' + dir })
      }

      return chips
    },

    activeFilterCount() {
      return this.activeFilterChips.length
    },

    pendingChangeCount() {
      if (!this.currentPreset || !this.pendingFilters) return 0
      var cur = this.currentPreset.context.filters
      var count = 0
      if (this.pendingFilters.dateFrom !== cur.dateFrom) count++
      if (this.pendingFilters.dateTo !== cur.dateTo) count++
      if (this.pendingFilters.dateField !== cur.dateField) count++
      if (JSON.stringify(this.pendingFilters.filterOn) !== JSON.stringify(cur.filterOn)) count++
      if (this.pendingExpr !== (this.currentPreset.context.expr || '')) count++
      return count
    },

    sortFieldOptions() {
      var options = [
        { value: 'created_at', text: this.$t('createdAt') },
        { value: 'updated_at', text: this.$t('updatedAt') }
      ]
      if (!this.availableFields) return options
      this.availableFields.forEach(function(f) {
        if (f.key && f.key !== 'created_at' && f.key !== 'updated_at' && f.key !== 'multiselect' && f.key !== 'actions' && !f.customListField) {
          options.push({ value: f.key, text: f.label || f.key })
        }
      })
      return options
    },

    currentSortLabel() {
      var self = this
      var opt = this.sortFieldOptions.find(function(o) { return o.value === self.sortBy })
      return opt ? opt.text : this.sortBy
    }
  },
  
  methods: {
      toggleSelectAll(checked) {
        if (checked) {
          var newSelections = this.currentPageItems.map(function(item) { return item.uuid }).filter(function(uuid) { return !this.itemsSelected.includes(uuid) }, this)
          this.itemsSelected.push.apply(this.itemsSelected, newSelections)
        } else {
          var currentPageUuids = this.currentPageItems.map(function(item) { return item.uuid })
          this.itemsSelected = this.itemsSelected.filter(function(uuid) { return !currentPageUuids.includes(uuid) })
        }
      },

      toggleFiltersPanel() {
        if (!this.filtersPanelOpen) {
          // Snapshot current filters into pending when opening
          if (this.currentPreset) {
            this.pendingFilters = _.cloneDeep(this.currentPreset.context.filters)
            this.pendingExpr = this.currentPreset.context.expr || ''
          }
        }
        this.filtersPanelOpen = !this.filtersPanelOpen
      },

      applyPendingFilters() {
        if (!this.currentPreset) return
        this.currentPreset.context.filters = Object.assign({}, this.currentPreset.context.filters, {
          dateField: this.pendingFilters.dateField,
          dateFrom: this.pendingFilters.dateFrom,
          dateTo: this.pendingFilters.dateTo,
          filterOn: this.pendingFilters.filterOn.slice(),
          $adv: this.pendingFilters.$adv
        })
        this.currentPreset.context.expr = this.pendingExpr
        this.filterOnChange()
      },

      applyPendingFiltersWithExpr(exprData) {
        this.pendingExpr = exprData
        this.applyPendingFilters()
      },

      resetPendingFilters() {
        if (!this.currentPreset) return
        this.pendingFilters = _.cloneDeep(this.currentPreset.context.filters)
        this.pendingExpr = this.currentPreset.context.expr || ''
      },

      clearSearch() {
        if (!this.currentPreset) return
        this.currentPreset.context.filters.filter = ''
        this.filterOnChange()
      },

      removeFilterChip(key) {
        if (!this.currentPreset) return
        if (key === 'dateFrom') {
          this.currentPreset.context.filters.dateFrom = null
          this.pendingFilters.dateFrom = null
        } else if (key === 'dateTo') {
          this.currentPreset.context.filters.dateTo = null
          this.pendingFilters.dateTo = null
        } else if (key === 'filterOn') {
          this.currentPreset.context.filters.filterOn = []
          this.pendingFilters.filterOn = []
        } else if (key === 'expr') {
          this.currentPreset.context.expr = ''
          this.pendingExpr = ''
        } else if (key === 'filter') {
          this.currentPreset.context.filters.filter = ''
        } else if (key === 'sort') {
          this.sortBy = 'created_at'
          this.sortDesc = true
        }
        this.filterOnChange()
      },

      clearAllFilters() {
        if (!this.currentPreset) return
        var def = {
          dateField: 'updated_at',
          dateFrom: _defaultDateFrom(),
          dateTo: _defaultDateTo(),
          filter: '',
          filterOn: [],
          $adv: {}
        }
        this.currentPreset.context.filters = Object.assign({}, this.currentPreset.context.filters, def)
        this.currentPreset.context.expr = ''
        this.pendingFilters = _.cloneDeep(def)
        this.pendingExpr = ''
        this.sortBy = 'created_at'
        this.sortDesc = true
        this.filterOnChange()
      },

      toggleSortDir() {
        this.sortDesc = !this.sortDesc
        this.filterOnChange()
      },

      onSortSelect(field) {
        if (field === this.sortBy) {
          this.sortDesc = !this.sortDesc
        } else {
          this.sortBy = field
          this.sortDesc = true
        }
        this.filterOnChange()
      },

      onSchemaChange() {
        // Store schema in localStorage
        const storageKey = `component_${this.componentID}_schema`
        localStorage.setItem(storageKey, this.selectedSchema || '')
        // Refresh the table with the new schema filter
        this.filterOnChange()
      },

      formattedValueForField(field, item) {
        try {
          return new Function('item', field.formatter)(item)
        } catch (error) {
          console.error('Error in field formatter:', error)
          return ''
        }
      },

    onSearch: _.debounce(function(text) {
      this.currentPreset.context.filters.filter = text
      this.filterOnChange()
    }, 500),

    getBadge(status) {
      return status === 'Active' ? 'success'
        : status === 'Inactive' ? 'secondary'
          : status === 'Pending' ? 'warning'
            : status === 'Banned' ? 'danger' : 'primary'
    },
    getRowCount: function () {
      return this.totalItems
    },

    edit(item) {
      if (this.$router) {
        this.$router.push({ path: "edit/" + item.uuid, append: true })
      }

      this.$emit('edit', item)
    },

    fetchData() {
      if (!this.currentPreset) return
      this.busy = true
      this.isRefreshing = true
      let expr = this.currentPreset.context.expr
      let filters = this.currentPreset.context.filters

      filters.perPage = 10
      filters.currentPage = this.page
      filters.sortDesc = this.sortDesc
      filters.sortBy = this.sortBy
      delete filters.cursor

      let result = safeEval("(function() { try {" + expr + " return filters;} catch(error) { console.log(error) } }())", {
        filters: new Filters(filters),
        _: _,
        moment: moment
      })

      if (result !== undefined) {
        // Mutate in-place so the existing reactive object reference is preserved.
        // Replacing the object with Object.assign({}, ...) creates a non-reactive plain
        // object, causing subsequent onSearch mutations to be invisible to Vue 2.
        Object.assign(this.currentPreset.context.filters, result)
      }

      var self = this

      // Compute componentID with schema appended if schema is selected
      let componentIdWithSchema = this.componentID
      if (this.selectedSchema) {
        componentIdWithSchema = `${this.componentID}.${this.selectedSchema}`
      }

      // Return cached result if still fresh — avoids refetch when navigating back
      const _cacheKey = _listCacheKey(componentIdWithSchema, this.currentPreset.context.filters)
      const _cached = _listCache.get(_cacheKey)
      if (_cached && (Date.now() - _cached.ts) < _LIST_CACHE_TTL) {
        this.totalItems = _cached.total
        this.pages = _cached.pages
        this.currentPageItems = _cached.items
        this.items = _cached.items
        this.busy = false
        this.isRefreshing = false

        _saveFilterState(componentIdWithSchema, {
          preset: this.filterPreset,
          page: this.page,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filters: this.currentPreset.context.filters,
        })
        return
      }

      // Cancel any in-flight list request before firing a new one
      if (this._listAbortController) {
        this._listAbortController.abort()
      }
      this._listAbortController = new AbortController()

      let streamedItems = []
      let streamedTotal = 0
      let streamedPages = 0

      const cursorKey = _cursorBaseKey(componentIdWithSchema, this.currentPreset.context.filters)
      const cursorMap = _cursorsByPage.get(cursorKey) || {}
      if (this.page > 1 && cursorMap[this.page]) {
        this.currentPreset.context.filters.cursor = cursorMap[this.page]
      }

      const applyDateFields = (i) => {
        i.created_at = this.$toUserTimeZone(i.created_at)
        i.updated_at = this.$toUserTimeZone(i.updated_at)
        i.deleted_at = this.$toUserTimeZone(i.deleted_at)
        i.disabled_at = this.$toUserTimeZone(i.disabled_at)
        if (self.customDateFields) {
          self.customDateFields.forEach(k => { i[k] = this.$toUserTimeZone(i[k]) })
        }
        return i
      }

      this.$store.dispatch('streamComponentsModels', {
        uuid: componentIdWithSchema,
        context: this.currentPreset.context.filters,
        buildTTL: 5,
        signal: this._listAbortController.signal,
        onEvent: (event) => {
          const d = event.data || {}
          if (event.type === 'metadata') {
            streamedTotal = d.total_filtered != null ? d.total_filtered : (d.total || 0)
            streamedPages = d.total_pages || 0
            this.totalItems = streamedTotal
            this.pages = streamedPages
          } else if (event.type === 'cursor') {
            if (d.next_cursor) {
              cursorMap[this.page + 1] = d.next_cursor
              _cursorsByPage.set(cursorKey, cursorMap)
            }
          } else if (event.type === 'record') {
            if (this.busy) this.busy = false
            streamedItems.push(applyDateFields(d))
            this.items = streamedItems.slice()
            this.currentPageItems = streamedItems.slice()
          }
        },
      }).then(() => {
        this.totalItems = streamedTotal
        this.pages = streamedPages
        this.currentPageItems = streamedItems
        this.items = streamedItems
        this.busy = false
        this.isRefreshing = false

        _listCache.set(_cacheKey, {
          ts: Date.now(),
          total: this.totalItems,
          pages: this.pages,
          items: streamedItems,
        })

        _saveFilterState(componentIdWithSchema, {
          preset: this.filterPreset,
          page: this.page,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filters: this.currentPreset.context.filters,
        })

      }).catch(err => {
        // Ignore aborted requests — a newer request superseded this one
        if (err && (err.code === 'ERR_CANCELED' || err.name === 'AbortError')) {
          return
        }

        this.busy = false
        this.isRefreshing = false

        console.error(err)

        if (err.response !== undefined) {
          if (err.response.status == 500) {
            this.$serverErrorModal(err)
            return
          }

          this.$mainDialog.msgBoxOk(err.response.data.message);
        }

        this.items = []
        this.totalItems = 0

        const errMsg = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : this.$t("unexpectedError")

        this.$bvToast.toast(errMsg, {
          title: this.$t("unexpectedErrorHappened"),
          variant: "danger"
        })
      })
    },

    filterOnChange() {
      this.fetchData()
    },

    goToCreate() {
      // Remove trailing slash if present, then append '/create'
      if (this.$route) {
        const basePath = this.$route.path.replace(/\/$/, '');
        this.$router.push({ path: `${basePath}/create` });
      }

      this.$emit('create')
    },

    quickActionDelete(e, item) {
      e.stopImmediatePropagation()

      let componentUuid = this.componentID

      this.$mainDialog.msgBoxConfirm(this.$t('areYouSure')).then((resp) => {
        if (resp) {
          const popup = this.$loadingPopup()

          this.$http.delete("/v3/system/component/" + componentUuid + "/model/" + item.uuid).then(() => {
            this.$store.commit("REMOVE_COMPONENTS_MODEL", { uuid: componentUuid, model_uuid: item.uuid })
            this.fetchData()
          }).catch((error) => {
            if (error.response === undefined) {
              this.$serverErrorModal(error)
              return
            }

            if (error.response.status == 500) {
              this.$serverErrorModal(error)
              return
            }

            this.$bvToast.toast(this.$t('somethingWrong'), {
              variant: 'warning',
              title: "Warning"
            })
          }).finally(() => {
            popup.hide()
          })
        }
      })

    },

    quickActionEdit(e, item) {
      e.stopImmediatePropagation()

      this.editModel.model = item
      this.editModel.show = true
    },

    multiAction(action) {
      let componentUuid = this.componentID

      this.$mainDialog.msgBoxConfirm(this.$t('areYouSure')).then((resp) => {
        if (resp) {
          this.$http.post("/v3/system/component/" + componentUuid + "/" + action + "/models", { items: this.itemsSelected }).then(() => {
            this.fetchData()
            this.itemsSelected = []
          }).catch((error) => {
            if (error.response === undefined) {
              this.$serverErrorModal(error)
              return
            }

            if (error.response.status == 500) {
              this.$serverErrorModal(error)
              return
            }

            this.$bvToast.toast(this.$t('somethingWrong'), {
              variant: 'warning',
              title: "Warning"
            })
          })
        }
      })
    },

    async transferToEnv() {
      const targetEnv = this.currentEnvIsLive ? 'dev' : 'live'
      const targetLabel = this.currentEnvIsLive ? 'Dev' : 'Live'
      const count = this.itemsSelected.length

      const confirmed = await this.$mainDialog.msgBoxConfirm(
        this.$t('transferConfirm', { count, env: targetLabel })
      )
      if (!confirmed) return

      const componentRef = this.componentID
      const systemFields = ['uuid', '_id', 'created_at', 'updated_at', 'disabled', '__v']
      let successCount = 0
      let errorCount = 0

      for (const uuid of this.itemsSelected) {
        try {
          const resp = await this.$http.get(`/v4/system/component/${componentRef}/model/${uuid}`)
          const modelData = resp.data && resp.data.data ? resp.data.data : resp.data

          const payload = {}
          for (const key of Object.keys(modelData)) {
            if (!systemFields.includes(key)) {
              payload[key] = modelData[key]
            }
          }

          await this.$http.post(`/v4/system/component/${componentRef}/model`, payload, {
            headers: { 'X-Project-Env': targetEnv }
          })
          successCount++
        } catch {
          errorCount++
        }
      }

      if (successCount > 0) {
        this.$bvToast.toast(
          this.$t('transferSuccess', { count: successCount, env: targetLabel }),
          { variant: 'success', title: this.$t('success') }
        )
      }
      if (errorCount > 0) {
        this.$bvToast.toast(
          this.$t('transferError', { count: errorCount }),
          { variant: 'warning', title: this.$t('warning') }
        )
      }

      this.itemsSelected = []
    },

    filterFieldForFormatter(field) {
      let fields = this.settings.fields;

      // find field by key
      let ff = fields.find(f => {
        return f.key == field
      })

      return ff
    },

    fieldCell(field) {
      //console.log(field)
      return `cell(${field})`;
    },

    forkIt() {
      let name = this.fork.name

      let newPreset = _.cloneDeep(this.currentPreset)
      newPreset.name = name

      let currentSettings = this.settings

      if (!currentSettings.presets) {
        currentSettings.presets = []
      }

      currentSettings.presets.push(newPreset);

      currentSettings.presets = currentSettings.presets.filter(p => {
        return p.name != 'Default'
      })

      this.$store.dispatch("updateComponentSettings", { ...this.component, settings: currentSettings }).then(() => {

        this.$bvToast.toast("Preset saved", {
          variant: 'success',
          title: "Success"
        })
      }).catch((err) => {
        this.$debugLog(err)
        this.$bvToast.toast("Failed to save preset", {
          variant: 'warning',
          title: "Warning"
        })

      })
    },

    updatePreset() {
      let presets = this.presets.map(p => {
        if (this.filterPreset == p.name) {
          return this.currentPreset
        }

        return p
      })

      let currentSettings = this.settings
      currentSettings.presets = presets;

      currentSettings.presets = currentSettings.presets.filter(p => {
        return p.name != 'Default'
      })

      this.$store.dispatch("updateComponentSettings", { ...this.component, settings: currentSettings }).then(() => {

        this.$bvToast.toast(this.$t('presetUpdated'), {
          variant: 'success',
          title: "Success"
        })
      }).catch(() => {
        this.$bvToast.toast(this.$t('failedUpdate'), {
          variant: 'warning',
          title: "Warning"
        })

      })

    },

    deletePreset() {
      let presets = this.presets.filter(p => {
        return this.currentPreset.name != p.name
      })

      let currentSettings = this.settings
      currentSettings.presets = presets;

      currentSettings.presets = currentSettings.presets.filter(p => {
        return p.name != 'Default'
      })

      this.$store.dispatch("updateComponentSettings", { ...this.component, settings: currentSettings }).then(() => {
        this.presets = presets;
        this.filterPreset = this.presets[0].name;

        this.$bvToast.toast(this.$t('presetRemoved', { name: this.currentPreset.name }), {
          variant: 'success',
          title: "Success"
        })
      }).catch(() => {

        this.$bvToast.toast(this.$t('failedRemove'), {
          variant: 'warning',
          title: "Warning"
        })
      })
    },

    setAsDefault() {
      let presets = this.presets.map(p => {
        p.context.default = false

        if (this.filterPreset == p.name) {
          p.context.default = true
        }

        return p
      })

      let currentSettings = this.settings
      currentSettings.presets = presets;
      currentSettings.presets = presets;

      currentSettings.presets = currentSettings.presets.filter(p => {
        return p.name != 'Default'
      })

      this.$store.dispatch("updateComponentSettings", { ...this.component, settings: currentSettings }).then(() => {
        this.presets = presets

        this.$bvToast.toast(this.$t('presetIsDefault', { name: this.filterPreset }), {
          variant: 'success',
          title: "success"
        })
      }).catch(() => {

        this.$bvToast.toast(this.$t('failedToSetDefault'), {
          variant: 'warning',
          title: "Warning"
        })
      })

    },

    applyAdvancedFilters(data) {
      this.currentPreset.context.expr = data
      this.fetchData()
    },

    runImport() {
      let file = this.importData.file
      let formData = new FormData();

      formData.append('file', file)
      formData.append('primary_key', this.importData.primaryKey || "")
      formData.append('field_map', JSON.stringify(this.importData.fieldMap) || [])

      this.$http.post("/v1/system/component/" + this.componentID + "/import", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then((resp) => {
        console.log(resp)
      }).catch(error => {
        if (error.response.status == 500) {
          this.$serverErrorModal(error)
          return
        }

        if (error.response.status == 417) {
          this.$mainDialog.msgBoxOk(this.$t("fieldsNotValid"))
          return
        }

        this.$mainDialog.msgBoxOk(error.response.data.message)
      })
    },

    runExport() {
      const items = this.items
      const blob = new Blob([JSON.stringify(items)], { type: 'text/plain' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = this.componentID + ".json"
      link.click()
      URL.revokeObjectURL(link.href)
    },

    addFieldMap() {
      this.importData.fieldMap.push({
        from: '',
        to: ''
      })
    },

    removeFieldMap(i) {
      this.importData.fieldMap.splice(i, 1)
    },

    _skeletonWidth(row, col) {
      var w = [55, 70, 40, 80, 45, 65, 35, 75, 50, 60];
      return w[(row * 3 + col * 7) % w.length] + '%';
    }
  },
  beforeDestroy() {
    if (this._listAbortController) {
      this._listAbortController.abort()
    }
    window.kortex.unregisterTool('reload_data')
    window.kortex.unregisterTool('search_list')
    window.kortex.unregisterTool('clear_search')
    window.kortex.unregisterTool('sort_list')
    window.kortex.unregisterTool('go_to_page')
    window.kortex.unregisterTool('get_list_state')
    window.kortex.unregisterTool('navigate_to_create')
  },

  mounted() {
    if (!this.$can("read " + this.component.general.getRef())) {
      if (!this.$isAdmin()) {
        this.$bvToast.toast(this.$t('noPermissionsToRead'), {
          title: this.$t('error'),
          variant: 'danger'
        })
      }
    }

    // ── Kortex tool registrations for the list page ────────────
    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'reload_data',
          description: 'Refreshes the records table on the current component list page, re-fetching data with the active filters.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => { this.fetchData() }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'search_list',
          description: 'Searches the list by setting a text filter. Pass an empty string to clear the search.',
          parameters: {
            type: 'object',
            required: ['query'],
            properties: {
              query: { type: 'string', description: 'The search text to apply.' }
            }
          }
        }
      },
      (args) => {
        this.onSearch(args.query || '')
        return { query: args.query || '' }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'clear_search',
          description: 'Clears the current search text and all active filters on the list page.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        this.clearAllFilters()
        return { cleared: true }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'sort_list',
          description: 'Sorts the list by a field. Use get_list_state to see available sort fields.',
          parameters: {
            type: 'object',
            required: ['field'],
            properties: {
              field: { type: 'string', description: 'The field key to sort by.' },
              descending: { type: 'boolean', description: 'Sort descending when true, ascending when false. Defaults to false.' }
            }
          }
        }
      },
      (args) => {
        var field = args.field
        var desc = args.descending === true
        this.onSortSelect(field)
        if (this.sortDesc !== desc) {
          this.sortDesc = desc
          this.fetchData()
        }
        return { sortBy: field, descending: desc }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'go_to_page',
          description: 'Navigates to a specific page number in the paginated list.',
          parameters: {
            type: 'object',
            required: ['page'],
            properties: {
              page: { type: 'integer', description: 'The 1-based page number to navigate to.' }
            }
          }
        }
      },
      (args) => {
        var p = parseInt(args.page, 10)
        if (p > 0) { this.page = p }
        return { page: this.page }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_list_state',
          description: 'Returns the current state of the list page: component name, active search query, sort field, sort direction, current page, total items, and available sort fields.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        var sortOptions = (this.sortFieldOptions || []).map(function (o) { return o.value })
        return {
          component: this.component && this.component.general && this.component.general.getRef(),
          search: this.currentPreset && this.currentPreset.context && this.currentPreset.context.filters && this.currentPreset.context.filters.filter,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          page: this.page,
          total_items: this.totalItems,
          available_sort_fields: sortOptions
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'navigate_to_create',
          description: 'Navigates to the create form for a new record on the current component list page.',
          parameters: { type: 'object', properties: {} }
        }
      },
      () => {
        this.goToCreate()
        return { navigated: true }
      }
    )

    this.presets = []

    //this.$store.dispatch("fetchComponentsSettings", this.$route.meta.uuid).then(data => {

    let settings = this.settings
    if (!settings.presets) {
      settings.presets = []
    }

    this.presets = settings.presets
    let restoredFromSession = false
    if (this.presets) {
      let foundDefault = this.presets.find(p => {
        return p.name == 'Default'
      })

      if (!foundDefault && (this.$isAdmin() || this.$store.getters.getCurrentEnv == 'dev')) {
        this.presets.push(this.defaultPreset)
      }

      let def = this.presets.find(p => {
        return p.context.default
      })

      let defaultPresetName = null
      if (def) {
        defaultPresetName = def.name
      } else {
        if (this.presets[0] !== undefined) {
          defaultPresetName = this.presets[0].name
        }
      }

      // Check for query preset first, then fall back to default
      let queryPreset = _.get(this.$route, 'query.preset', null)

      // Initialize selected schema from localStorage
      const baseRef = this.id || (this.$route ? this.$route.meta.ref : null)
      const storageKey = `component_${baseRef}_schema`
      const storedSchema = localStorage.getItem(storageKey)
      this.selectedSchema = storedSchema !== null ? storedSchema : ''

      // Restore filter state from sessionStorage (survives navigate-away-and-back)
      let componentIdWithSchema = this.componentID
      if (this.selectedSchema) {
        componentIdWithSchema = `${this.componentID}.${this.selectedSchema}`
      }
      const savedState = _restoreFilterState(componentIdWithSchema)

      if (savedState && !queryPreset) {
        const targetPresetName = savedState.preset
        const targetPreset = this.presets.find(p => p.name === targetPresetName)

        if (targetPreset && savedState.filters) {
          Object.assign(targetPreset.context.filters, savedState.filters)
        }

        // Restore sort state
        if (savedState.sortBy) this.sortBy = savedState.sortBy
        if (savedState.sortDesc !== undefined) this.sortDesc = savedState.sortDesc

        // Now set filterPreset — the watcher will clone the already-restored filters
        this.filterPreset = targetPreset ? targetPresetName : (queryPreset || defaultPresetName)
        restoredFromSession = true

        // Restore page via query param
        if (savedState.page && savedState.page > 1 && this.$router) {
          this.$router.replace({ query: { ...this.$route.query, page: savedState.page, preset: this.filterPreset } })
        }
      } else {
        this.filterPreset = queryPreset || defaultPresetName
      }

      // Initialize pending filters to match the current preset
      if (this.currentPreset) {
        this.pendingFilters = _.cloneDeep(this.currentPreset.context.filters)
        this.pendingExpr = this.currentPreset.context.expr || ''
      }
    }

    // Migrate stale presets — skip if we just restored from session
    if (!restoredFromSession && this.currentPreset) {
      const f = this.currentPreset.context.filters
      if (!f.dateFrom || !f.dateTo || f.dateField === 'created_at') {
        f.dateField = 'updated_at'
        f.dateFrom = _defaultDateFrom()
        f.dateTo = _defaultDateTo()
      }
    }

    this.fetchData()
    this._mountingState = false

    //})


  },

}
</script>